import React from "react";
import { useController } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/Components/ui/select";
import { Clock } from "lucide-react";

// Generate hours array (00-23)
const generateHours = () => {
  return Array.from({ length: 24 }, (_, i) => ({
    value: i.toString().padStart(2, "0"),
    label: i.toString().padStart(2, "0"),
  }));
};

// Generate minutes array (00-59)
const generateMinutes = () => {
  return Array.from({ length: 60 }, (_, i) => ({
    value: i.toString().padStart(2, "0"),
    label: i.toString().padStart(2, "0"),
  }));
};

export function TimePicker24Hour({ name, control, placeholder = "00:00" }) {
  const { field } = useController({
    name,
    control,
  });

  // Parse time value (HH:mm format)
  const parseTime = (timeValue) => {
    if (!timeValue || timeValue === "") {
      return { hours: "00", minutes: "00" };
    }
    // Handle different time formats
    const timeStr = String(timeValue).trim();
    const [hours = "00", minutes = "00"] = timeStr.split(":");
    return {
      hours: hours.padStart(2, "0"),
      minutes: minutes.padStart(2, "0"),
    };
  };

  // Format time value (HH:mm format)
  const formatTime = (hours, minutes) => {
    return `${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
  };

  const currentTime = parseTime(field.value || "");
  const hours = generateHours();
  const minutes = generateMinutes();

  const handleHoursChange = (newHours) => {
    const formattedTime = formatTime(newHours, currentTime.minutes);
    field.onChange(formattedTime);
  };

  const handleMinutesChange = (newMinutes) => {
    const formattedTime = formatTime(currentTime.hours, newMinutes);
    field.onChange(formattedTime);
  };

  return (
    <div dir="ltr" className="flex items-center gap-2 w-full">
      <Clock className="w-4 h-4 text-muted-foreground shrink-0" />
      <div className="flex items-center gap-2 flex-1">
        {/* Hours Select */}
        <Select value={currentTime.hours} onValueChange={handleHoursChange}>
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="00" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {hours.map((hour) => (
              <SelectItem key={hour.value} value={hour.value}>
                {hour.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Separator */}
        <span className="text-lg font-semibold text-muted-foreground">:</span>

        {/* Minutes Select */}
        <Select
          dir="ltr"
          value={currentTime.minutes}
          onValueChange={handleMinutesChange}
        >
          <SelectTrigger className="flex-1">
            <SelectValue placeholder="00" />
          </SelectTrigger>
          <SelectContent className="max-h-[200px]">
            {minutes.map((minute) => (
              <SelectItem key={minute.value} value={minute.value}>
                {minute.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
